import { useMemo, useReducer, useState } from 'react'
import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'
import TableWrapper from '@/components/common/table/TableWrapper'
import IndeterminateCheckbox from '@/components/common/IndeterminateCheckbox'
import StatusPill from '@/components/common/StatusPill'
import Tooltip from '@/components/common/Tooltip'
import Panel from '@/components/common/Panel'
import PaymentMethodLogo from '@/components/billing/PaymentMethodLogo'
import {
  deletePaymentMethod,
  deletePaymentMethodList,
  getPaymentMethods,
  placeholderData,
  PaymentMethod,
  ExportFilterPaymentMethod,
  prepareForExport,
} from '@/lib/paymentmethod/PaymentMethod'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid'
import { className2 } from '@/components/common/styles'
import { performExport } from '@/components/common/table/performExport'
import type {
  TableWrapperCellContext,
  TableWrapperColumnDef,
  TableWrapperHeaderContext,
} from '@/lib/table/table'

const PaymentMethods = () => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['getPaymentMethods'],
    queryFn: getPaymentMethods,
    placeholderData,
  })
  console.log('data=', data)
  const [exportData, setExportData] = useState(
    [] as ExportFilterPaymentMethod[],
  )
  const rerender = useReducer(() => ({}), {})[1]

  const columns: TableWrapperColumnDef<PaymentMethod>[] = useMemo(
    () => [
      {
        accessorKey: 'select',
        id: 'select',
        enableSorting: false,
        header: (info: TableWrapperHeaderContext<PaymentMethod>) => (
          <div className="px-5">
            <IndeterminateCheckbox
              aria-label="select all"
              {...{
                checked: info.table.getIsAllRowsSelected(),
                indeterminate:
                  info.table.getIsSomeRowsSelected() &&
                  !info.table.getIsAllRowsSelected(),
                onChange: info.table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </div>
        ),
        cell: (info: TableWrapperCellContext<PaymentMethod>) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                'aria-label': 'select',
                checked: info.row.getIsSelected(),
                indeterminate: info.row.getIsSomeSelected(),
                onChange: info.row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        accessorKey: 'date_added',
        id: 'date_added',
        header: () => <div>Date added</div>,
        cell: (info) => <div>{info.getValue<string>()}</div>,
      },
      {
        accessorKey: 'payment_method',
        id: 'payment_method',
        header: () => <div>Payment method</div>,
        cell: (info) => (
          <div className="flex">
            <PaymentMethodLogo paymentMethod={info.getValue() as string} />
            <div className="mr-1" />
            {info.getValue<string>()}
          </div>
        ),
      },
      {
        accessorKey: 'paypal_email_address',
        id: 'paypal_email_address',
        header: () => <div>PayPal email address</div>,
        cell: (info) => (
          <div>
            {info.getValue() ? (
              <div>
                {'with: '}
                {info.getValue<string | undefined>()}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'creditcard_info',
        id: 'creditcard_info',
        cell: (info) => (
          <div>
            {info.getValue() ? (
              <div>
                {'ending in: '}
                {info.getValue<string | undefined>()}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'creditcard_expiration_date',
        id: 'creditcard_expiration_date',
        cell: (info) => (
          <div className="flex">
            {info.getValue<string | undefined>()}
            <Tooltip message="Creditcard is expired.">
              <ExclamationTriangleIcon className="text-red-500 w-4 h-4 mx-1 mt-1" />
            </Tooltip>
          </div>
        ),
      },
      {
        accessorKey: 'primary',
        id: 'primary',
        cell: (info) => (
          <div>
            {info.getValue() ? (
              <div>
                <StatusPill
                  value="primary"
                  className="text-black dark:text-white"
                  icon={
                    <CheckCircleIcon className="text-green-500 h-4 w-4 mt-0 mx-1" />
                  }
                />
              </div>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'action',
        id: 'action',
        cell: (info: TableWrapperCellContext<PaymentMethod>) => (
          <div>
            <button
              type="button"
              className={className2}
              onClick={() => {
                console.log('clicked button for row=', info.row)
                performExport(prepareForExport([info.row.original]))
              }}
            >
              Download
            </button>
            <button
              type="button"
              className={className2}
              onClick={() => {
                console.log('row.original.id=', info.row.original.id)
                const result = deletePaymentMethod(info.row.original.id)
                console.log('result=', result)
                rerender() // @@TODO: Should not need this if useQuery is used properly
              }}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [rerender],
  )

  //const memoizedData = useMemo(() => data ?? [], [data])
  const memoizedData = useMemo<PaymentMethod[]>(() => {
    return isLoading ? placeholderData : data
  }, [isLoading, data])

  return (
    <div className="p-4">
      <Panel title="Payment methods">
        <div>
          <TableWrapper
            data={memoizedData}
            columns={columns}
            exportData={exportData}
            deleteWithList={deletePaymentMethodList}
            prepareForExport={prepareForExport}
            name="payment methods"
          />
          <div className="pb-8">
            <Link href="#!">
              <div className="text-blue-600 dark:text-blue-400 hover:underline decoration-2">
                Read more about our payment methods
              </div>
            </Link>
          </div>
        </div>
      </Panel>
    </div>
  )
}

export default PaymentMethods
